import React from "react";
import LoadingSpinner from "./LoadingSpinner";
import LeagueErrorPage from "./LeagueErrorPage";
import ErrorBanner from "./UI/ErrorBanner";
import {
  TABLE_ROW,
  TABLE_HEADER_ROW,
  HEADER_CELL,
  BODY_CELL,
} from "./UI/tableStyles";
import { getChipMeta } from "./UI/chipMeta";
import { useLeagueStandings } from "../hooks/useLeagueStandings";
import { useTeamContext } from "../contexts/TeamContext";

const ChipIndicator: React.FC<{ chip: string }> = ({ chip }) => {
  if (!chip) return null;
  const { Icon, label, iconClass } = getChipMeta(chip);
  const description = `${label} played this gameweek`;

  return (
    <span
      role="img"
      aria-label={description}
      title={description}
      className={`shrink-0 ${iconClass}`}
    >
      <Icon aria-hidden />
    </span>
  );
};

const LeagueStandings: React.FC = () => {
  const { leagueId } = useTeamContext();
  const { rows, loading, error } = useLeagueStandings(leagueId);

  if (!leagueId) return <LeagueErrorPage />;
  if (loading)
    return (
      <div className="flex justify-center items-center w-full h-full">
        <LoadingSpinner />
      </div>
    );
  if (error) return <ErrorBanner message={error} className="mx-4" />;

  return (
    <div className="mx-4 bg-surface rounded-xl p-4 text-content">
      <h2 className="text-2xl font-bold mb-4">League Standings</h2>
      <div className="overflow-x-auto">
        <table className="w-full table-fixed">
          <thead>
            <tr className={TABLE_HEADER_ROW}>
              <th className={`w-10 md:w-20 ${HEADER_CELL} text-left`}>
                <span className="md:hidden">#</span>
                <span className="hidden md:inline">Rank</span>
              </th>
              <th className={`${HEADER_CELL} text-left`}>Team</th>
              <th className={`w-12 md:w-28 ${HEADER_CELL} text-left`}>
                <span className="md:hidden">Cpt</span>
                <span className="hidden md:inline">Captain</span>
              </th>
              <th className={`w-10 md:w-16 ${HEADER_CELL} text-right`}>GW</th>
              <th className={`w-12 md:w-20 ${HEADER_CELL} text-right`}>
                <span className="md:hidden">Tot</span>
                <span className="hidden md:inline">Total</span>
              </th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {rows.map((team) => (
              <tr key={team.entry} className={TABLE_ROW}>
                <td className={`${BODY_CELL} text-left`}>{team.live_rank}</td>
                <td className={`${BODY_CELL} text-left overflow-hidden`}>
                  <div className="truncate">{team.entry_name}</div>
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-accent">{team.player_name}</span>
                    <ChipIndicator chip={team.active_chip} />
                  </div>
                </td>
                <td
                  className={`${BODY_CELL} text-left text-xs text-content-muted truncate`}
                >
                  {(team.captain_pick_name ?? "").slice(0, 4)}
                </td>
                <td className={`${BODY_CELL} text-right font-semibold`}>
                  {team.live_event_points}
                </td>
                <td className={`${BODY_CELL} text-right font-semibold`}>
                  {team.live_total}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* tiny legend */}
      <p className="mt-3 text-xs text-content-secondary">
        Live points include captain/TC and Bench Boost. Vice-captain takes over
        if the captain hasn’t played. Full autosubs &amp; official bonus are applied
        by FPL later.
      </p>
    </div>
  );
};

export default LeagueStandings;
